import { useEffect, useState } from 'react'
import { getServicingBorrowerMessages, replyServicingBorrowerMessage } from 'services'
import { svgLoading } from 'stories/assets'
import { Button } from 'stories/components'
import { formatTime } from 'utils'
import { RenderInput } from 'utils/RenderInput'

/**
 * The secure message thread a borrower and the servicer exchange through the borrower portal,
 * plus the staff reply box.
 *
 * Held apart from the screen that frames it because two places render it: the Borrower Portal tab
 * of Servicing > Messages, and the `borrowerPortal` configurable page a tenant may already have
 * built. Neither owns the thread, so neither carries its own copy of the fetch and reply calls.
 *
 * The thread is scoped by the `loan-number` header, like every other servicing call.
 */
export const BorrowerPortalMessageThread = () => {
  const [messages, setMessages] = useState<any[]>([])
  const [reply, setReply] = useState('')
  const [isLoading, setLoading] = useState(true)
  const [isSubmitting, setSubmitting] = useState(false)

  const load = () => {
    setLoading(true)
    getServicingBorrowerMessages()
      .then((res) => setMessages(res?.messages || []))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [])

  const onSend = async () => {
    if (!reply.trim()) return
    setSubmitting(true)
    try {
      const message = await replyServicingBorrowerMessage(reply.trim())
      setMessages([...messages, message])
      setReply('')
    } catch (error) {
      console.error('Failed to send reply:', error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <div className="space-y-3 mb-4 max-h-[24rem] overflow-y-auto pr-1">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <img src={svgLoading} className="w-6 h-6 animate-spin" alt="loading" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-6 text-gray-500">No messages from the borrower yet.</div>
        ) : (
          messages.map((m, index) => {
            const fromStaff = m.senderType === 'staff'
            return (
              <div key={m.id ?? index} className={`flex ${fromStaff ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[80%] rounded-lg p-3 border ${
                    fromStaff ? 'bg-shade-blue/10 border-shade-blue/20' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900 text-sm">
                      {fromStaff ? m.senderName || 'You' : m.senderName || m.senderEmail || 'Borrower'}
                    </span>
                    <span className="text-xs text-gray-500">{m.createdAt ? formatTime(m.createdAt) : ''}</span>
                  </div>
                  <div className="text-gray-700 whitespace-pre-wrap break-words text-sm">{m.body}</div>
                </div>
              </div>
            )
          })
        )}
      </div>

      <div className="border-t border-gray-200 pt-4">
        <RenderInput
          input={{
            inputType: 'textarea',
            type: 'text',
            title: '',
            value: reply,
            required: false,
            placeholder: 'Reply to the borrower...',
            rows: 3,
          }}
          Key="borrowerReply"
          onChange={(_key: string, value: string) => setReply(value)}
        />
        <div className="flex justify-end mt-2">
          <Button onClick={onSend} disabled={!reply.trim() || isSubmitting} className="!py-2 !px-4">
            {isSubmitting ? 'Sending...' : 'Send Reply'}
          </Button>
        </div>
      </div>
    </div>
  )
}
